import React, {useState} from 'react';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {View, Text, TextInput, TouchableOpacity, FlatList, ScrollView} from 'react-native';

import {text} from '../text';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {components} from '../components';
import type {RootStackParamList} from '../types';
import {
  useGetProductsQuery,
  useGetCategoriesQuery,
} from '../store/slices/apiSlice';
import {useAppSelector} from '../hooks';
import {emitLocationPickerEvent} from '../utils/locationEvents';

type Props = NativeStackScreenProps<RootStackParamList, 'Menulist'>;

const Menulist: React.FC<Props> = ({route}): JSX.Element => {
  const {category} = route.params;

  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(category || '');
  const [searchQuery, setSearchQuery] = useState('');
  const locationState = useAppSelector((state) => state.locationSlice);

  const {
    data: productsData,
    error: productsError,
    isLoading: productsLoading,
  } = useGetProductsQuery();
  const {
    data: categoriesData,
    error: categoriesError,
    isLoading: categoriesLoading,
  } = useGetCategoriesQuery();

  const dishes = Array.isArray(productsData) ? productsData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  if (loading) {
    return <components.Loader />;
  }

  const handleLocationRequest = () => {
    emitLocationPickerEvent();
  };

  const renderLocationRequired = () => {
    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          paddingHorizontal: 20,
        }}
        showsVerticalScrollIndicator={false}
      >
        <text.H3 style={{textAlign: 'center', color: theme.colors.textPrimary, marginBottom: 12}}>
          Veuillez entrez une adresse
        </text.H3>
        <text.T16 style={{textAlign: 'center', color: theme.colors.textMuted, marginBottom: 20}}>
          Sélectionnez une adresse pour consulter le menu détaillé.
        </text.T16>
        <components.Button
          title='Choisir une adresse'
          onPress={handleLocationRequest}
          containerStyle={{alignSelf: 'stretch'}}
        />
      </ScrollView>
    );
  };

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return <components.Header goBack={true} title='Menu' basket={true} />;
  };

  const renderSearchBar = () => {
    return (
      <View
        style={{
          marginTop: 10,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginHorizontal: 20,
          height: 50,
          marginBottom: 14,
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.backgroundPrimary,
            flex: 1,
            borderRadius: 10,
            marginRight: 5,
            padding: 5,
            flexDirection: 'row',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <View
            style={{
              backgroundColor: theme.colors.accentPrimary,
              width: 40,
              height: '100%',
              borderRadius: 8,
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: 14,
            }}
          >
            <svg.SearchSvg />
          </View>
          <TextInput
            placeholder='Search ...'
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={{
              flex: 1,
              ...theme.fonts.DMSans_400Regular,
              fontSize: 16,
              color: theme.colors.textPrimary,
            }}
            placeholderTextColor={theme.colors.textMuted}
            autoCorrect={false}
            autoCapitalize='none'
            clearButtonMode='while-editing'
          />
        </View>
        <TouchableOpacity
          style={{
            backgroundColor: theme.colors.backgroundPrimary,
            borderRadius: 10,
            height: 50,
            width: 50,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <svg.FilterSvg />
        </TouchableOpacity>
      </View>
    );
  };

  const renderCategories = () => {
    return (
      <FlatList
        data={categories}
        horizontal={true}
        decelerationRate={0}
        contentContainerStyle={{
          paddingLeft: 20,
          marginBottom: 20,
        }}
        style={{
          flexGrow: 0,
        }}
        showsHorizontalScrollIndicator={false}
        renderItem={({item, index}) => {
          const last = index === categories.length - 1;
          return (
            <TouchableOpacity
              style={{
                paddingHorizontal: 20,
                paddingVertical: 10,
                backgroundColor: theme.colors.backgroundPrimary,
                borderRadius: 10,
                marginRight: last ? 20 : 8,
                borderWidth: 1,
                borderColor:
                  selectedCategory === item.id
                    ? theme.colors.accentPrimary
                    : theme.colors.backgroundPrimary,
              }}
              onPress={() => {
                setSelectedCategory((prev) =>
                  prev === item.id ? '' : item.id,
                );
              }}
            >
              <text.H5
                style={{
                  color:
                    selectedCategory === item.id
                      ? theme.colors.accentPrimary
                      : theme.colors.textPrimary,
                }}
              >
                {item.name}
              </text.H5>
            </TouchableOpacity>
          );
        }}
      />
    );
  };

  const renderContent = () => {
    if (!locationState.isAddressConfirmed) {
      return renderLocationRequired();
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const selectedCategoryName = categories.find(
      (categoryItem) => categoryItem.id === selectedCategory,
    )?.name;
    const dishesByCategory = dishes?.filter((dish) => {
      const normalizedMenus = Array.isArray(dish.menu) ? dish.menu : [];
      const isInCategory = selectedCategory
        ? normalizedMenus.includes(selectedCategory) ||
          (selectedCategoryName
            ? normalizedMenus.includes(selectedCategoryName)
            : false)
        : true;
      const dishName = dish.name ?? '';
      const matchesSearch = normalizedQuery
        ? dishName.toLowerCase().includes(normalizedQuery)
        : true;
      return isInCategory && matchesSearch;
    });
    if (!dishesByCategory?.length) {
      return (
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 20,
            paddingTop: 40,
            alignItems: 'center',
          }}
          showsVerticalScrollIndicator={false}
        >
          <text.T16 style={{color: theme.colors.textMuted, textAlign: 'center'}}>
            Aucun plat ne correspond à votre recherche.
          </text.T16>
        </ScrollView>
      );
    }

    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 20,
        }}
        showsVerticalScrollIndicator={false}
      >
        {dishesByCategory?.map((item, index, array) => {
          const lastItem = index === array.length - 1;
          return (
            <components.MenuListItem
              item={item}
              lastItem={lastItem}
              key={item.id}
            />
          );
        })}
      </ScrollView>
    );
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderSearchBar()}
      {renderCategories()}
      {renderContent()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Menulist;
